import {
  ImportedPriceSuggestionPrice,
  PriceSuggestionPrice,
  PriceSuggestionPriceActionDTO,
  PriceSuggestionPriceItem,
} from '@retail/price-suggestion/types';
import { useToast } from '@retail/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useMutateCreatePriceSuggestionPrices } from '@retail/price-suggestion/data-access';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useAppTFunction } from '@retail/app/i18n';
import { getISO_8601Date } from '@retail/utils';

const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(yesterday.getDate() - 1);

export function useLocalPriceSuggestionActions() {
  const t = useAppTFunction();
  const toast = useToast();
  const { assortmentId } = useSelectedOrgUnit();
  const queryClient = useQueryClient();

  const { mutateAsync: postPriceSuggestionPrices, isPending: isPendingPostPriceSuggestions } =
    useMutateCreatePriceSuggestionPrices({
      assortmentId,
    });

  const createLocalPriceSuggestions = (selectedItems: ImportedPriceSuggestionPrice[]) => {
    postPriceSuggestionPrices({ body: selectedItems })
      .then(() => {
        toast.success(t('priceSuggestion.actions.priceSuggestionsAdded'));
        queryClient.invalidateQueries({ queryKey: ['price-suggestion-prices'] });
      })
      .catch((e) => {
        console.error(e);
        toast.error(t('calculus.error.generic'));
      });
  };

  const editLocalPriceSuggestions = (updatedItems: PriceSuggestionPriceItem[]) => {
    const items: PriceSuggestionPriceActionDTO[] = updatedItems.map((item) => {
      return {
        mgItemNumber: item.mgItemNumber,
        mgSupplierId: item.mgSupplierId,
        price: item.price as number,
        validFrom: getISO_8601Date(item.validFrom),
        validTo: item.validTo ? getISO_8601Date(item.validTo) : undefined,
      };
    });
    postPriceSuggestionPrices({
      body: items,
    })
      .then(() => {
        toast.success(t('priceSuggestion.actions.priceSuggestionsUpdated'));
        queryClient.invalidateQueries({ queryKey: ['price-suggestion-prices'] });
      })
      .catch((err) => {
        console.error(err);
        toast.error(t('calculus.error.generic'));
      });
  };

  const deleteLocalPriceSuggestions = (itemsToDelete: PriceSuggestionPrice[]) => {
    const items = itemsToDelete.map((item) => {
      return {
        mgItemNumber: item.mgSupplierItem.mgItemNumber,
        mgSupplierId: item.mgSupplierItem.mgParticipantAccount.mgSupplierId,
        validFrom: item.validFrom,
        price: item.price,
        validTo: getISO_8601Date(yesterday),
      };
    });
    postPriceSuggestionPrices({
      body: items,
    })
      .then(() => {
        toast.success(t('priceSuggestion.actions.priceSuggestionDeleted'));
        queryClient.invalidateQueries({ queryKey: ['price-suggestion-prices'] });
      })
      .catch((err) => {
        console.error(err);
        toast.error(t('calculus.error.generic'));
      });
  };

  return {
    createLocalPriceSuggestions,
    editLocalPriceSuggestions,
    deleteLocalPriceSuggestions,
    isPendingPostPriceSuggestions,
  };
}
